import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { Upload, Download, FileSpreadsheet, CheckCircle, XCircle,
  AlertTriangle, Loader2, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import { downloadImportTemplate, validateImport, executeImport } from '@/lib/api';

const PREVIEW_COLS = [
  'manufacturer_name', 'manufacturer_part_number', 'article_number',
  'quantity', 'uom', 'number_of_rolls', 'site'
];

export const BulkUploadCard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [expanded, setExpanded] = useState(false);
  const [step, setStep] = useState(0); // 0: idle, 1: preview, 2: validating, 3: configure, 4: importing, 5: done
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importMode, setImportMode] = useState('enrich');
  const [dragOver, setDragOver] = useState(false);

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadImportTemplate();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'material_import_vorlage.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('bulk.templateDownloaded'));
    } catch {
      toast.error(t('bulk.templateError'));
    }
  };

  const parseFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws);
        setRawData(data);
        setStep(1);
        toast.success(t('bulk.rowsLoaded', { count: data.length }));
      } catch {
        toast.error(t('bulk.parseError'));
      }
    };
    reader.readAsBinaryString(file);
  }, [t]);

  const handleFileChange = (e) => parseFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv'))) {
      parseFile(file);
    } else {
      toast.error(t('bulk.invalidFile'));
    }
  };

  const handleValidate = async () => {
    setStep(2);
    setLoading(true);
    try {
      const res = await validateImport(rawData);
      setValidationResult(res.data);
      setStep(3);
    } catch {
      toast.error(t('bulk.validateError'));
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    setStep(4);
    setLoading(true);
    try {
      const res = await executeImport(validationResult.valid_rows, importMode, null);
      setImportResult(res.data);
      setStep(5);
      toast.success(t('bulk.importComplete'));
    } catch {
      toast.error(t('bulk.importError'));
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0);
    setRawData([]);
    setValidationResult(null);
    setImportResult(null);
    setImportMode('enrich');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Card className="mb-6" data-testid="bulk-upload-card">
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => setExpanded(!expanded)}
        data-testid="bulk-upload-toggle"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-orange-500" />
            <div>
              <CardTitle className="text-lg">{t('bulk.title')}</CardTitle>
              <CardDescription className="text-xs mt-0.5">{t('bulk.subtitle')}</CardDescription>
            </div>
          </div>
          {expanded ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4">
          {/* Step 0: Upload Area */}
          {step === 0 && (
            <>
              <div className="flex gap-2 mb-3">
                <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                  <Download className="h-4 w-4 mr-1" />
                  {t('bulk.downloadTemplate')}
                </Button>
              </div>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragOver ? 'border-orange-400 bg-orange-50 dark:bg-orange-950/20' : 'border-muted'}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                data-testid="bulk-dropzone"
              >
                <FileSpreadsheet className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-3">{t('bulk.dropHint')}</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="bulk-file-input"
                />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="browse-file-btn">
                  {t('bulk.browse')}
                </Button>
              </div>
            </>
          )}

          {/* Step 1: Preview */}
          {step === 1 && (
            <>
              <div className="flex items-center justify-between mb-2">
                <Badge variant="outline">{rawData.length} {t('bulk.rows')}</Badge>
              </div>
              <ScrollArea className="h-48 border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs w-8">#</TableHead>
                      {PREVIEW_COLS.map(col => (
                        <TableHead key={col} className="text-xs">{t(`bulk.col.${col}`)}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rawData.slice(0, 8).map((row, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                        {PREVIEW_COLS.map(col => (
                          <TableCell key={col} className="text-xs">{row[col] ?? '-'}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
              {rawData.length > 8 && <p className="text-xs text-muted-foreground text-center">+{rawData.length - 8} {t('bulk.moreRows')}</p>}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={reset}>{t('common.cancel')}</Button>
                <Button size="sm" onClick={handleValidate} className="bg-orange-500 hover:bg-orange-600" data-testid="validate-btn">
                  {t('bulk.validate')}
                </Button>
              </div>
            </>
          )}

          {/* Step 2: Validating */}
          {step === 2 && (
            <div className="flex items-center justify-center py-8 gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
              <span className="text-sm">{t('bulk.validating')}</span>
            </div>
          )}

          {/* Step 3: Validation Result + Import Mode */}
          {step === 3 && validationResult && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/20 text-center">
                  <p className="text-xl font-bold text-green-600">{validationResult.valid_count}</p>
                  <p className="text-xs text-muted-foreground">{t('bulk.valid')}</p>
                </div>
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 text-center">
                  <p className="text-xl font-bold text-red-600">{validationResult.error_count}</p>
                  <p className="text-xs text-muted-foreground">{t('bulk.errors')}</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 text-center">
                  <p className="text-xl font-bold text-amber-600">{validationResult.valid_rows?.filter(r => r.is_duplicate).length || 0}</p>
                  <p className="text-xs text-muted-foreground">{t('bulk.duplicates')}</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 text-center">
                  <p className="text-xl font-bold text-blue-600">{validationResult.valid_rows?.filter(r => !r.is_duplicate).length || 0}</p>
                  <p className="text-xs text-muted-foreground">{t('bulk.newItems')}</p>
                </div>
              </div>

              {validationResult.errors?.length > 0 && (
                <Alert variant="destructive" className="mt-3">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>{t('bulk.validationErrors')}</AlertTitle>
                  <AlertDescription className="text-xs">
                    {validationResult.errors.slice(0, 5).map((err, i) => (
                      <div key={i}>{t('bulk.rowError', { row: err.row })}: {err.errors.join(', ')}</div>
                    ))}
                    {validationResult.errors.length > 5 && <div className="mt-1">...+{validationResult.errors.length - 5}</div>}
                  </AlertDescription>
                </Alert>
              )}

              {validationResult.valid_count > 0 && (
                <div className="space-y-3 pt-2">
                  <Label className="text-sm font-medium">{t('bulk.importMode')}</Label>
                  <RadioGroup value={importMode} onValueChange={setImportMode} className="space-y-2">
                    <div className="flex items-center space-x-2 p-2 border rounded-lg">
                      <RadioGroupItem value="enrich" id="bulk-enrich" />
                      <Label htmlFor="bulk-enrich" className="cursor-pointer text-sm">
                        <span className="font-medium">{t('bulk.modeEnrich')}</span>
                        <span className="text-xs text-muted-foreground ml-1">- {t('bulk.modeEnrichDesc')}</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-2 border rounded-lg">
                      <RadioGroupItem value="overwrite" id="bulk-overwrite" />
                      <Label htmlFor="bulk-overwrite" className="cursor-pointer text-sm">
                        <span className="font-medium">{t('bulk.modeOverwrite')}</span>
                        <span className="text-xs text-muted-foreground ml-1">- {t('bulk.modeOverwriteDesc')}</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={reset}>{t('common.cancel')}</Button>
                {validationResult.valid_count > 0 && (
                  <Button size="sm" onClick={handleExecute} className="bg-orange-500 hover:bg-orange-600" data-testid="execute-import-btn">
                    {t('bulk.executeImport', { count: validationResult.valid_count })}
                  </Button>
                )}
              </div>
            </>
          )}

          {/* Step 4: Importing */}
          {step === 4 && (
            <div className="flex items-center justify-center py-8 gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
              <span className="text-sm">{t('bulk.importing')}</span>
            </div>
          )}

          {/* Step 5: Done */}
          {step === 5 && importResult && (
            <>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="font-semibold text-sm">{t('bulk.importComplete')}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/20 text-center">
                  <p className="text-xl font-bold text-green-600">{importResult.created}</p>
                  <p className="text-xs text-muted-foreground">{t('bulk.created')}</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 text-center">
                  <p className="text-xl font-bold text-blue-600">{importResult.updated + importResult.overwritten}</p>
                  <p className="text-xs text-muted-foreground">{t('bulk.updated')}</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 text-center">
                  <p className="text-xl font-bold text-amber-600">{importResult.enriched}</p>
                  <p className="text-xs text-muted-foreground">{t('bulk.enriched')}</p>
                </div>
                {importResult.rejected_count > 0 && (
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 text-center">
                    <p className="text-xl font-bold text-red-600">{importResult.rejected_count}</p>
                    <p className="text-xs text-muted-foreground">{t('bulk.rejected')}</p>
                  </div>
                )}
              </div>
              {importResult.rejected_count > 0 && (
                <Alert variant="destructive" className="mt-3">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    {importResult.rejected?.slice(0, 3).map((r, i) => <div key={i}>{r.error}</div>)}
                  </AlertDescription>
                </Alert>
              )}
              <Button variant="outline" size="sm" onClick={reset} className="mt-2" data-testid="import-again-btn">
                <RefreshCw className="h-4 w-4 mr-1" />
                {t('bulk.importMore')}
              </Button>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
};
